import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { mjBizAppsIssuesIssueTypeEntity } from '@mj-biz-apps/issues-entities';
import { BCSaaSOrganizationPersonRoleEntity } from '@bluecypress/bcsaas-entities';
import { IzzySkillEntity, IzzyPersonSkillEntity, IzzySkillIssueTypeEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

/** A skill plus its expand/edit state and the related rows it owns. */
interface SkillDisplay {
  skill: IzzySkillEntity;
  isEditing: boolean;
  expanded: boolean;
  form: { name: string; description: string; status: 'Active' | 'Inactive' };
  /** IssueType mappings for this skill (which ticket types require it). */
  typeMaps: IzzySkillIssueTypeEntity[];
  /** Per-person proficiencies for this skill. */
  proficiencies: IzzyPersonSkillEntity[];
  /** New-mapping draft. */
  newTypeId: string;
  newTypeMin: number | null;
  /** New-proficiency draft. */
  newPersonId: string;
  newPersonLevel: number;
}

/**
 * Skills admin panel (PRD v4.0 §9.4 — skills-based routing). Org-scoped management of the three
 * skill entities the SkillBased assignment mode consumes:
 *   - izzy.Skill          — the org's skills,
 *   - izzy.SkillIssueType — which ticket types require which skills (+ optional min proficiency),
 *   - izzy.PersonSkill    — each agent's proficiency (1-5) per skill.
 *
 * Each skill row expands to manage its issue-type mappings and per-person proficiencies inline.
 */
@Component({
  standalone: false,
  selector: 'izzy-skills',
  templateUrl: './skills.component.html',
  styleUrls: ['./skills.component.css'],
})
export class SkillsComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected skills: SkillDisplay[] = [];
  protected issueTypes: mjBizAppsIssuesIssueTypeEntity[] = [];
  protected people: mjBizAppsCommonPersonEntity[] = [];

  protected readonly levels = [1, 2, 3, 4, 5];

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  protected isCreating = false;
  protected createForm = { name: '', description: '', status: 'Active' as 'Active' | 'Inactive' };

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected async loadData(): Promise<void> {
    if (!this.organizationId) {
      this.skills = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await this.loadIssueTypes();
      await this.loadPeople();

      const rv = new RunView();
      const skillRes = await rv.RunView<IzzySkillEntity>({
        EntityName: 'Skills',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        OrderBy: 'Name ASC',
        ResultType: 'entity_object',
      });
      const skills = skillRes.Success ? skillRes.Results : [];

      // Load all type-maps + proficiencies for these skills in two queries, then group.
      const skillIds = skills.map(s => s.ID);
      const [maps, profs] = skillIds.length
        ? await Promise.all([this.loadTypeMaps(skillIds), this.loadProficiencies(skillIds)])
        : [[], []];

      this.skills = skills.map(s => ({
        skill: s,
        isEditing: false,
        expanded: false,
        form: { name: s.Name, description: s.Description || '', status: s.Status as 'Active' | 'Inactive' },
        typeMaps: maps.filter(m => UUIDsEqual(m.SkillID, s.ID)),
        proficiencies: profs.filter(p => UUIDsEqual(p.SkillID, s.ID)),
        newTypeId: '',
        newTypeMin: null,
        newPersonId: '',
        newPersonLevel: 3,
      }));

      this.statusChange.emit(
        this.skills.length === 0
          ? { status: 'none', tooltip: 'No skills defined' }
          : { status: 'good', tooltip: `${this.skills.filter(s => s.skill.Status === 'Active').length} active skill(s)` }
      );
    } catch (error) {
      console.error('Skills: failed to load', error);
      this.errorMessage = 'Failed to load skills.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadTypeMaps(skillIds: string[]): Promise<IzzySkillIssueTypeEntity[]> {
    const rv = new RunView();
    const list = skillIds.map(id => `'${id}'`).join(', ');
    const res = await rv.RunView<IzzySkillIssueTypeEntity>({
      EntityName: 'Skill Issue Types',
      ExtraFilter: `SkillID IN (${list})`,
      ResultType: 'entity_object',
    });
    return res.Success ? res.Results : [];
  }

  private async loadProficiencies(skillIds: string[]): Promise<IzzyPersonSkillEntity[]> {
    const rv = new RunView();
    const list = skillIds.map(id => `'${id}'`).join(', ');
    const res = await rv.RunView<IzzyPersonSkillEntity>({
      EntityName: 'Person Skills',
      ExtraFilter: `SkillID IN (${list})`,
      ResultType: 'entity_object',
    });
    return res.Success ? res.Results : [];
  }

  private async loadIssueTypes(): Promise<void> {
    const rv = new RunView();
    const res = await rv.RunView<mjBizAppsIssuesIssueTypeEntity>({
      EntityName: 'MJ_BizApps_Issues: Issue Types',
      OrderBy: 'Name ASC',
      ResultType: 'entity_object',
    });
    this.issueTypes = res.Success ? res.Results : [];
  }

  /** Org People (for the proficiency person picker). */
  private async loadPeople(): Promise<void> {
    const rv = new RunView();
    const oprRes = await rv.RunView<BCSaaSOrganizationPersonRoleEntity>({
      EntityName: 'BC: Organization Person Roles',
      ExtraFilter: `OrganizationID = '${this.organizationId}'`,
      Fields: ['PersonID'],
    });
    const personIds = Array.from(
      new Set((oprRes.Success ? oprRes.Results : []).map(r => r.PersonID).filter(Boolean))
    );
    if (personIds.length === 0) {
      this.people = [];
      return;
    }
    const list = personIds.map(id => `'${id}'`).join(', ');
    const peopleRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${list})`,
      OrderBy: 'LastName ASC',
      ResultType: 'entity_object',
    });
    this.people = peopleRes.Success ? peopleRes.Results : [];
  }

  // ========== Display helpers ==========

  typeName(typeId: string | null): string {
    if (!typeId) return '';
    return this.issueTypes.find(t => UUIDsEqual(t.ID, typeId))?.Name || 'Unknown';
  }

  personName(personId: string | null): string {
    if (!personId) return '';
    const p = this.people.find(x => UUIDsEqual(x.ID, personId));
    if (!p) return 'Unknown';
    return p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Unknown';
  }

  toggleExpand(d: SkillDisplay): void {
    d.expanded = !d.expanded;
  }

  // ========== Skill CRUD ==========

  startCreate(): void {
    this.isCreating = true;
    this.createForm = { name: '', description: '', status: 'Active' };
  }

  cancelCreate(): void {
    this.isCreating = false;
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.name.trim()) {
      this.errorMessage = 'Skill name is required.';
      return;
    }
    if (!this.organizationId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const s = await md.GetEntityObject<IzzySkillEntity>('Skills');
      s.NewRecord();
      s.OrganizationID = this.organizationId;
      s.Name = this.createForm.name.trim();
      s.Description = this.createForm.description.trim() || null;
      s.Status = this.createForm.status;
      if (!await s.Save()) {
        this.errorMessage = s.LatestResult?.CompleteMessage || 'Failed to create skill.';
        return;
      }
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Skill created');
    } catch (error) {
      console.error('Skills: create failed', error);
      this.errorMessage = 'Failed to create skill.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(d: SkillDisplay): void {
    d.isEditing = true;
    d.form = { name: d.skill.Name, description: d.skill.Description || '', status: d.skill.Status as 'Active' | 'Inactive' };
  }

  cancelEdit(d: SkillDisplay): void {
    d.isEditing = false;
  }

  async saveEdit(d: SkillDisplay): Promise<void> {
    if (!d.form.name.trim()) {
      this.errorMessage = 'Skill name is required.';
      return;
    }
    this.isSaving = true;
    try {
      d.skill.Name = d.form.name.trim();
      d.skill.Description = d.form.description.trim() || null;
      d.skill.Status = d.form.status;
      if (!await d.skill.Save()) {
        this.errorMessage = d.skill.LatestResult?.CompleteMessage || 'Failed to update skill.';
        return;
      }
      d.isEditing = false;
      this.showSuccess('Skill updated');
    } catch (error) {
      console.error('Skills: edit failed', error);
      this.errorMessage = 'Failed to update skill.';
    } finally {
      this.isSaving = false;
    }
  }

  async deleteSkill(d: SkillDisplay): Promise<void> {
    this.isSaving = true;
    try {
      // Remove dependent rows first so the FK references don't block the skill delete.
      for (const m of d.typeMaps) { await m.Delete(); }
      for (const p of d.proficiencies) { await p.Delete(); }
      if (!await d.skill.Delete()) {
        this.errorMessage = 'Failed to delete skill.';
        return;
      }
      this.skills = this.skills.filter(x => x !== d);
      this.showSuccess('Skill deleted');
    } catch (error) {
      console.error('Skills: delete failed', error);
      this.errorMessage = 'Failed to delete skill.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Type mappings ==========

  async addTypeMap(d: SkillDisplay): Promise<void> {
    if (!d.newTypeId) {
      this.errorMessage = 'Choose an issue type to map.';
      return;
    }
    if (d.typeMaps.some(m => UUIDsEqual(m.IssueTypeID, d.newTypeId))) {
      this.errorMessage = 'That issue type is already mapped to this skill.';
      return;
    }
    this.isSaving = true;
    try {
      const md = new Metadata();
      const m = await md.GetEntityObject<IzzySkillIssueTypeEntity>('Skill Issue Types');
      m.NewRecord();
      m.SkillID = d.skill.ID;
      m.IssueTypeID = d.newTypeId;
      m.MinProficiency = d.newTypeMin ?? null;
      if (!await m.Save()) {
        this.errorMessage = m.LatestResult?.CompleteMessage || 'Failed to add mapping.';
        return;
      }
      d.typeMaps = [...d.typeMaps, m];
      d.newTypeId = '';
      d.newTypeMin = null;
      this.showSuccess('Issue type mapped');
    } catch (error) {
      console.error('Skills: addTypeMap failed', error);
      this.errorMessage = 'Failed to add mapping.';
    } finally {
      this.isSaving = false;
    }
  }

  async removeTypeMap(d: SkillDisplay, m: IzzySkillIssueTypeEntity): Promise<void> {
    this.isSaving = true;
    try {
      if (!await m.Delete()) {
        this.errorMessage = 'Failed to remove mapping.';
        return;
      }
      d.typeMaps = d.typeMaps.filter(x => x !== m);
    } catch (error) {
      console.error('Skills: removeTypeMap failed', error);
      this.errorMessage = 'Failed to remove mapping.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Per-person proficiency ==========

  async addProficiency(d: SkillDisplay): Promise<void> {
    if (!d.newPersonId) {
      this.errorMessage = 'Choose a person.';
      return;
    }
    if (d.proficiencies.some(p => UUIDsEqual(p.PersonID, d.newPersonId))) {
      this.errorMessage = 'That person already has a proficiency for this skill.';
      return;
    }
    this.isSaving = true;
    try {
      const md = new Metadata();
      const p = await md.GetEntityObject<IzzyPersonSkillEntity>('Person Skills');
      p.NewRecord();
      p.PersonID = d.newPersonId;
      p.SkillID = d.skill.ID;
      p.ProficiencyLevel = d.newPersonLevel;
      if (!await p.Save()) {
        this.errorMessage = p.LatestResult?.CompleteMessage || 'Failed to add proficiency.';
        return;
      }
      d.proficiencies = [...d.proficiencies, p];
      d.newPersonId = '';
      d.newPersonLevel = 3;
      this.showSuccess('Proficiency added');
    } catch (error) {
      console.error('Skills: addProficiency failed', error);
      this.errorMessage = 'Failed to add proficiency.';
    } finally {
      this.isSaving = false;
    }
  }

  async updateProficiency(p: IzzyPersonSkillEntity, level: number): Promise<void> {
    this.isSaving = true;
    try {
      p.ProficiencyLevel = level;
      if (!await p.Save()) {
        this.errorMessage = 'Failed to update proficiency.';
      }
    } catch (error) {
      console.error('Skills: updateProficiency failed', error);
      this.errorMessage = 'Failed to update proficiency.';
    } finally {
      this.isSaving = false;
    }
  }

  async removeProficiency(d: SkillDisplay, p: IzzyPersonSkillEntity): Promise<void> {
    this.isSaving = true;
    try {
      if (!await p.Delete()) {
        this.errorMessage = 'Failed to remove proficiency.';
        return;
      }
      d.proficiencies = d.proficiencies.filter(x => x !== p);
    } catch (error) {
      console.error('Skills: removeProficiency failed', error);
      this.errorMessage = 'Failed to remove proficiency.';
    } finally {
      this.isSaving = false;
    }
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadSkillsComponent(): void {}
